import { cn } from '@/lib/cn';
import { For, type JSX, Show } from 'solid-js';
import { Skeleton } from './Skeleton';

export interface TableColumn<T> {
  key: string;
  header: string;
  render: (row: T) => JSX.Element;
  width?: string;
  align?: 'left' | 'right' | 'center';
}

export interface TableProps<T> {
  data: readonly T[];
  columns: readonly TableColumn<T>[];
  rowKey: (row: T) => string;
  empty?: JSX.Element;
  loading?: boolean;
  onRowClick?: (row: T) => void;
  /** Marks "you are here": brand tint + aria-current, and onRowClick is skipped for it. */
  current?: (row: T) => boolean;
}

export function Table<T>(props: TableProps<T>): JSX.Element {
  return (
    <div class="overflow-x-auto rounded-xl border border-neutral-100 bg-surface shadow-sm dark:border-neutral-800 dark:bg-surface-dark">
      <table class="w-full border-collapse text-sm">
        <thead class="border-b border-neutral-200 bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800">
          <tr class="divide-x divide-neutral-200 dark:divide-neutral-700">
            <For each={props.columns}>
              {(col) => (
                <th
                  class={cn(
                    'px-4 py-3 text-left font-medium text-neutral-600 dark:text-neutral-400',
                    col.align === 'right' && 'text-right',
                    col.align === 'center' && 'text-center',
                  )}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.header}
                </th>
              )}
            </For>
          </tr>
        </thead>
        <tbody class="divide-y divide-neutral-200 dark:divide-neutral-800">
          <Show when={!props.loading && props.data.length === 0 && props.empty}>
            <tr>
              <td colspan={props.columns.length} class="px-4 py-12 text-center">
                {props.empty}
              </td>
            </tr>
          </Show>
          <Show when={props.loading && props.data.length === 0}>
            <For each={[0, 1, 2]}>
              {() => (
                <tr class="divide-x divide-neutral-100 dark:divide-neutral-800">
                  <For each={props.columns}>
                    {() => (
                      <td class="px-4 py-3">
                        <Skeleton class="h-4" />
                      </td>
                    )}
                  </For>
                </tr>
              )}
            </For>
          </Show>
          <For each={props.data}>
            {(row) => (
              <tr
                class={cn(
                  'divide-x divide-neutral-100 transition-colors dark:divide-neutral-800',
                  props.current?.(row)
                    ? 'bg-brand-50/50 dark:bg-brand-900/20'
                    : 'even:bg-neutral-50/60 hover:divide-neutral-200 hover:bg-neutral-100 dark:even:bg-neutral-900/20 dark:hover:divide-neutral-700 dark:hover:bg-neutral-800/40',
                  props.onRowClick && !props.current?.(row) && 'cursor-pointer',
                )}
                aria-current={props.current?.(row) ? 'true' : undefined}
                onClick={(e) => {
                  // Ignore clicks on interactive elements (links, buttons, checkboxes…)
                  // so row-level actions don't double-fire.
                  if (e.target.closest('a,button,input,label,select,textarea')) return;
                  if (props.current?.(row)) return;
                  props.onRowClick?.(row);
                }}
              >
                <For each={props.columns}>
                  {(col) => (
                    <td
                      class={cn(
                        'px-4 py-3',
                        col.align === 'right' && 'text-right',
                        col.align === 'center' && 'text-center',
                      )}
                    >
                      {col.render(row)}
                    </td>
                  )}
                </For>
              </tr>
            )}
          </For>
        </tbody>
      </table>
    </div>
  );
}
